import { useTranslation } from 'react-i18next'
import { BetTypeEnum, DigitMode, LottoBetList } from '../BetLotto.types'
import {
  AddLottoListHandler,
  DeleteByNumberAndTypeHandler,
  BetTypeOption,
  DigitModeOption,
  GetLimitTypeUIHandler,
} from './PanelSelection.types'
import { useMemo } from 'react'
import ThreePanel from './SubPanel/ThreePanel/ThreePanel'
import TwoPanel from './SubPanel/TwoPanel/TwoPanel'
import RunningPanel from './SubPanel/RunningPanel/RunningPanel'

export interface PanelSelectionProps {
  DIGIT_MODES: DigitModeOption[]
  handleSelectDigitMode: (mode: DigitMode) => void
  digitMode: DigitMode
  betTypeOptions: BetTypeOption[]
  activeBetType: BetTypeEnum[]
  handleSelectBetType: (type: BetTypeEnum) => void
  betList: LottoBetList[]
  addLottoListAndHandleResponse: AddLottoListHandler
  onDeleteByNumberAndType: DeleteByNumberAndTypeHandler
  getLimitTypeUI: GetLimitTypeUIHandler
}

export const PanelSelection: React.FC<PanelSelectionProps> = ({
  DIGIT_MODES,
  handleSelectDigitMode,
  digitMode,
  betTypeOptions,
  activeBetType,
  handleSelectBetType,
  betList,
  addLottoListAndHandleResponse,
  onDeleteByNumberAndType,
  getLimitTypeUI,
}) => {
  const { t } = useTranslation()
  const DigitModePanel = useMemo(() => {
    switch (digitMode) {
      case DigitMode.THREE_DIGIT:
        return (
          <ThreePanel
            activeBetType={activeBetType}
            betList={betList}
            addLottoListAndHandleResponse={addLottoListAndHandleResponse}
            onDeleteByNumberAndType={onDeleteByNumberAndType}
            getLimitTypeUI={getLimitTypeUI}
          />
        )
      case DigitMode.TWO_DIGIT:
        return (
          <TwoPanel
            activeBetType={activeBetType}
            betList={betList}
            addLottoListAndHandleResponse={addLottoListAndHandleResponse}
            onDeleteByNumberAndType={onDeleteByNumberAndType}
            getLimitTypeUI={getLimitTypeUI}
          />
        )
      case DigitMode.RUNNING:
        return (
          <RunningPanel
            activeBetType={activeBetType}
            betList={betList}
            addLottoListAndHandleResponse={addLottoListAndHandleResponse}
            onDeleteByNumberAndType={onDeleteByNumberAndType}
            getLimitTypeUI={getLimitTypeUI}
          />
        )
      default:
        return null
    }
  }, [
    digitMode,
    betList,
    addLottoListAndHandleResponse,
    activeBetType,
    onDeleteByNumberAndType,
    getLimitTypeUI,
  ])

  return (
    <div className="p-6">
      <div className="mb-6">
        <h5 className="text-[var(--caption-text)] text-base font-medium mb-4 text-left">
          {t('betDetailPanel.selectNumberDigits')}
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {DIGIT_MODES.map((m) => (
            <button
              type="button"
              key={m.value}
              className={`py-3 px-2 sm:px-4 rounded-lg border font-semibold text-base transition-all cursor-pointer ${
                digitMode === m.value
                  ? 'btn-active-state border-[var(--bet-type-active-border)]'
                  : 'bg-[var(--bet-type-bg)] text-[var(--bet-type-text)] border-[var(--bet-type-border)] hover:bg-[var(--bet-type-hover-bg)] hover:text-[var(--bet-type-hover-text)] hover:border-[var(--bet-type-hover-border)]'
              }`}
              onClick={() => handleSelectDigitMode(m.value)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <hr className="my-6 border-[var(--panel-border)]" />
      <div className="mb-6">
        <h5 className="text-[var(--caption-text)] text-base font-medium mb-4 text-left">
          {t('betDetailPanel.selectBetType')}
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {betTypeOptions.map((option) => (
            <div
              data-group="button"
              key={option.value}
              onClick={() => handleSelectBetType(option.value)}
              className={`flex items-center rounded-lg border transition-all group ${
                activeBetType.includes(option.value)
                  ? 'bet-type-active-state border-[var(--bet-type-active-border)]'
                  : 'bg-[var(--bet-type-bg)] border-[var(--bet-type-border)] hover:bg-[var(--bet-type-hover-bg)] hover:text-[var(--bet-type-hover-text)] hover:border-[var(--bet-type-hover-border)]'
              }`}
            >
              <button
                type="button"
                className={`flex-1 px-4 py-3 text-left font-semibold text-base rounded-lg cursor-pointer ${
                  activeBetType.includes(option.value)
                    ? ''
                    : 'text-[var(--bet-type-text)] group-hover:text-[var(--bet-type-hover-text)]'
                }`}
              >
                {option.label}
              </button>
              <div
                className={`px-4 py-3 text-right font-semibold text-base cursor-pointer ${
                  activeBetType.includes(option.value)
                    ? ''
                    : 'text-[var(--bet-type-text)] group-hover:text-[var(--bet-type-hover-text)]'
                }`}
              >
                {option.rate}
              </div>
            </div>
          ))}
        </div>
      </div>
      <hr className="my-6 border-[var(--panel-border)]" />
      {DigitModePanel}
    </div>
  )
}

export default PanelSelection
